import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, type MouseEvent } from 'react';
import Script from 'next/script';
import { useIsVisible } from '@sbt-web/hooks';
import type { ItemMap } from '@sbt-web/network/types';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import { buildTrackingFilterData, buildTrackingPOIData, buildTrackingViewData, buildTrackingFullScreenData, buildTrackingZoomData } from '@/tools/tracking/yatmoMapTracking/buildTrackingData';
import classes from './YatmoMap.module.scss';
interface Props {
  map?: ItemMap;
}
const observerOptionsTop = {
  threshold: 0.01
};
const observerOptionsMiddle = {
  threshold: 0.5
};
const YatmoMap = t0 => {
  const $ = _c(19);
  const {
    map
  } = t0;
  const element = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      observerOptions: observerOptionsTop
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [isVisibleTop, setElementTop, unobserveTop] = useIsVisible(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      observerOptions: observerOptionsMiddle
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [isVisibleMiddle, setElementMiddle, unobserveMiddle] = useIsVisible(t2);
  let t3;
  let t4;
  if ($[2] !== isVisibleTop || $[3] !== setElementTop || $[4] !== unobserveTop) {
    t3 = () => {
      if (element.current !== null) {
        setElementTop(element.current);
        if (isVisibleTop) {
          getOrCreatePulse()?.queueEvent(buildTrackingViewData("Top"));
          unobserveTop();
        }
      }
    };
    t4 = [isVisibleTop, setElementTop, unobserveTop];
    $[2] = isVisibleTop;
    $[3] = setElementTop;
    $[4] = unobserveTop;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== isVisibleMiddle || $[8] !== setElementMiddle || $[9] !== unobserveMiddle) {
    t5 = () => {
      if (element.current !== null) {
        setElementMiddle(element.current);
        if (isVisibleMiddle) {
          getOrCreatePulse()?.queueEvent(buildTrackingViewData("Middle"));
          unobserveMiddle();
        }
      }
    };
    t6 = [isVisibleMiddle, setElementMiddle, unobserveMiddle];
    $[7] = isVisibleMiddle;
    $[8] = setElementMiddle;
    $[9] = unobserveMiddle;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  if (!map?.longitude || !map?.latitude) {
    return null;
  }
  const t7 = `const yatmoConfig = {
            licenseKey: '${process.env.NEXT_PUBLIC_YATMO_WEB_KEY}',
            language: 'IT',
            country: 'IT',
            container: 'map',
            center: [${map.longitude}, ${map.latitude}],
            zoom: 14,
            mapStyle: 3,
            fullScreenButton: true,
            useSmallButtons:false
          }

          window.LoadMap = (map) => {
            new mapboxgl.Marker({ color: 'red' })
              .setLngLat(map.getCenter())
              .addTo(map);
          };`;
  let t8;
  if ($[12] !== t7) {
    t8 = <Script id="yatmo-config">{t7}</Script>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t10;
  let t11;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Script src="https://map.yatmo.com/script.js" />;
    t10 = <h6 className="headline-6">Posizione</h6>;
    t11 = <div id="map" className={classes.frame} ref={element} onClick={trackClickEvent} role="application" />;
    $[14] = t10;
    $[15] = t11;
    $[16] = t9;
  } else {
    t10 = $[14];
    t11 = $[15];
    t9 = $[16];
  }
  let t12;
  if ($[17] !== t8) {
    t12 = <>{t8}{t9}{t10}{t11}</>;
    $[17] = t8;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
};
export const trackClickEvent = (event: MouseEvent): void => {
  const eventTarget = event.target as HTMLElement;
  // Click on the map's button filters
  if (eventTarget instanceof HTMLElement && eventTarget.parentElement?.id === 'poisFilterContainer') {
    trackPoisFilterClick(eventTarget);
  }
  // Click on the map's POI
  else if (eventTarget.dataset.poitypeid) {
    const poiTypeId = eventTarget.dataset.poitypeid;
    const poiId = eventTarget.id;
    trackMapPoiClick(poiTypeId, poiId);
  }
  // Click on the map's FullScreen button
  else if (eventTarget.parentElement?.ariaLabel?.includes('fullscreen')) {
    getOrCreatePulse()?.queueEvent(buildTrackingFullScreenData(eventTarget.parentElement.className?.includes('fullscreen') ? 'Open' : 'Close'));
  }
  // Click on the map's Zoom buttons
  else if (eventTarget.parentElement?.ariaLabel?.includes('Zoom')) {
    getOrCreatePulse()?.queueEvent(buildTrackingZoomData(eventTarget.parentElement.className?.includes('zoom-in') ? 'In' : 'Out'));
  }
};
function trackMapPoiClick(poiTypeId: string, poiId: string) {
  switch (poiTypeId[3]) {
    case '1':
      getOrCreatePulse()?.queueEvent(buildTrackingPOIData('Educazione', poiId));
      break;
    case '2':
      getOrCreatePulse()?.queueEvent(buildTrackingPOIData(poiTypeId[7] === '5' ? 'Autostrade' : 'Trasporti', poiId));
      break;
    case '3':
      getOrCreatePulse()?.queueEvent(buildTrackingPOIData('Shopping', poiId));
      break;
    default:
      break;
  }
}
function trackPoisFilterClick(eventTarget: HTMLElement) {
  switch (eventTarget.id) {
    case 'bt-kids':
      getOrCreatePulse()?.queueEvent(buildTrackingFilterData('Educazione'));
      break;
    case 'bt-transport':
      getOrCreatePulse()?.queueEvent(buildTrackingFilterData('Trasporti'));
      break;
    case 'bt-motorways':
      getOrCreatePulse()?.queueEvent(buildTrackingFilterData('Autostrade'));
      break;
    case 'bt-shops':
      getOrCreatePulse()?.queueEvent(buildTrackingFilterData('Shopping'));
      break;
    default:
      break;
  }
}
export default YatmoMap;